import { useQuery, useQueryClient } from 'react-query';

import { getV2ApiCompanyIdRequestsDraft } from 'src/client/spendesk-api';
import { type GetV2ApiCompanyIdRequestsDraftResponseOK } from 'src/client/spendesk-api-types';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';

export const requestsDraftQueryKey = ['requests', 'draft'];

export const useRequestsDraftQuery = () => {
  const company = useCompany();
  return useQuery(
    requestsDraftQueryKey,
    async () => {
      const response = await getV2ApiCompanyIdRequestsDraft({
        path: { companyId: company.id },
        query: {
          orderingKey: 'createdAt',
          orderingOrder: 'desc',
          enrichRequestReceipts: 'true',
          enrichRequestReceiptExtractedInformations: 'true',
        },
      });

      if (response.statusCode !== 200) {
        throw new Error('Failed to fetch recipient requirements');
      }

      return response.body;
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
};

export const useRemoveRequestsFromRequestsDraftCache = () => {
  const queryClient = useQueryClient();

  return (requestIds: string[]) => {
    const data =
      queryClient.getQueryData<GetV2ApiCompanyIdRequestsDraftResponseOK>(
        requestsDraftQueryKey,
      );
    if (data) {
      queryClient.setQueryData<
        GetV2ApiCompanyIdRequestsDraftResponseOK | undefined
      >(requestsDraftQueryKey, (oldData) => {
        if (!oldData) {
          return undefined;
        }

        const updatedRequests = oldData.data.requests.filter(
          (request) => !requestIds.includes(request.id),
        );

        return {
          data: {
            ...oldData.data,
            requests: updatedRequests,
          },
        };
      });
    }
  };
};
